import React, { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Row, Col, Heading, LoadingSpinner, SubHeading } from 'reaxl';
import { Link as LinkButton } from '@iris/react-components';
import {
    WALLET_GET_DEAL_DETAILS_COMPLETED,
    WALLET_GET_DEAL_DETAILS_REQUEST,
    WALLET_GET_DEAL_DETAILS_WITH_POLLING_REQUEST,
} from 'wallet-manager';
import { eventBus } from '@atc/cai-event-bus';
import clsx from 'clsx';

import DealCard from '../DealCard';

function DealReceipt({
    dealsDuck,
    inventoryDuck,
    isUDSCompact,
    onEmailCTAClick = () => {},
    paymentsDuck,
    vin,
    ...rest
}) {
    const walletId = useSelector(paymentsDuck?.selectors?.getWalletId);
    const existingDeal = useSelector((state) => dealsDuck?.selectors?.getDealByListingVin(state, vin)) || {};
    const { deal, vehicle, isEcommerce = false } = existingDeal;

    const [isLoading, setIsLoading] = useState(false);
    const enableLoading = () => setIsLoading(true);
    const disableLoading = () => setIsLoading(false);

    useEffect(() => {
        eventBus.on(WALLET_GET_DEAL_DETAILS_WITH_POLLING_REQUEST, enableLoading);
        eventBus.on(WALLET_GET_DEAL_DETAILS_REQUEST, enableLoading);
        eventBus.on(WALLET_GET_DEAL_DETAILS_COMPLETED, disableLoading);

        return () => {
            eventBus.off(WALLET_GET_DEAL_DETAILS_WITH_POLLING_REQUEST, enableLoading);
            eventBus.off(WALLET_GET_DEAL_DETAILS_REQUEST, enableLoading);
            eventBus.off(WALLET_GET_DEAL_DETAILS_COMPLETED, disableLoading);
        };
    }, []);

    const handleRetry = () => {
        eventBus.emit(WALLET_GET_DEAL_DETAILS_REQUEST, { walletId });
    };

    const handleActiveWalletPanel = () => {
        onEmailCTAClick('walletCreditApp');
    };
    const inventory = useSelector((state) => inventoryDuck?.selectors?.getInventory(state)) || {};
    const selectedInventory = Object.keys(inventory).filter((listingId) => inventory[listingId].vin === vin);

    let dealWithListing;

    if (Object.keys(existingDeal).length) {
        dealWithListing = {
            ...existingDeal,
            vehicle: {
                ...vehicle,
                ...inventory[selectedInventory?.[0]],
            },
            dealer: inventory[selectedInventory?.[0]]?.owner,
        };
    }

    const dealType = deal?.dealType;
    let dealHeading = 'Offer Overview';

    if (isEcommerce) {
        dealHeading = 'Order Overview';
    } else if (dealType) {
        dealHeading = `${dealType.charAt(0).toUpperCase() + dealType.slice(1)} Offer Overview`;
    }

    return (
        <div data-cmp="dealReceipt">
            {isLoading && <LoadingSpinner className="margin-vertical-7" />}
            {!isLoading && (
                <>
                    {dealWithListing && (
                        <div
                            data-cmp="dealOverview"
                            className={clsx('padding-4', {
                                'panel panel-default': isUDSCompact,
                            })}
                        >
                            {isUDSCompact ? (
                                <SubHeading
                                    level={2}
                                    className="text-center"
                                >
                                    {dealHeading}
                                </SubHeading>
                            ) : (
                                <Heading
                                    className="text-center"
                                    size={600}
                                    color="primary"
                                >
                                    {dealHeading}
                                </Heading>
                            )}
                            <Row>
                                <Col
                                    xs={12}
                                    md={isUDSCompact ? 12 : 6}
                                    mdOffset={isUDSCompact ? 0 : 3}
                                    className={clsx({ 'margin-top-5': isUDSCompact })}
                                >
                                    <DealCard
                                        dealData={dealWithListing}
                                        paymentsDuck={paymentsDuck}
                                        dealsDuck={dealsDuck}
                                        indexClicked={0}
                                        uiContext="receipt"
                                        handleActiveWalletPanel={handleActiveWalletPanel}
                                        onEmailCTAClick={onEmailCTAClick}
                                        isCompactView={isUDSCompact}
                                        {...rest}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                    {/*
                     * Edge case if we end up in an odd state where the parent component
                     * thinks there is a deal but this component can't fetch details
                     */}
                    {!dealWithListing && (
                        <Alert
                            bsStyle="warning"
                            className="margin-3 margin-md-7"
                        >
                            Whoops! We were unable to fetch your deal details.&nbsp;
                            <LinkButton
                                variant="standalone"
                                className="iris:inline"
                                onClick={handleRetry}
                            >
                                Try again
                            </LinkButton>
                            .
                        </Alert>
                    )}
                </>
            )}
        </div>
    );
}

export default memo(DealReceipt);
